import React from "react";
import { QRCodeSVG } from "qrcode.react";

/**
 * Printable player credential card. Front side shows core ID + QR.
 * Used for event check-in at CBR camps.
 */
export default function QRCredentialCard({ player, qrUrl, campTitle, playerNumber }) {
  if (!player) return null;
  return (
    <div
      data-testid="qr-credential-card"
      className="bg-white border-4 border-[#0A192F] w-[340px] rounded-sm overflow-hidden print:border-2 print:shadow-none"
      style={{ pageBreakInside: "avoid" }}
    >
      {/* Header */}
      <div className="bg-[#0A192F] text-white px-4 py-3 flex items-center justify-between">
        <div>
          <div className="font-display text-sm uppercase tracking-tight">CBR Prospects</div>
          <div className="text-[9px] uppercase tracking-[0.2em] text-white/60">Player Credential</div>
        </div>
        <div className="w-10 h-10 bg-[#E63946] grid place-items-center font-display text-white text-xs">CBR</div>
      </div>

      {/* Body */}
      <div className="p-4 flex gap-3">
        {/* Photo */}
        <div className="w-20 h-24 bg-slate-200 border border-slate-300 grid place-items-center overflow-hidden flex-shrink-0">
          {player.profile_photo_url ? (
            <img src={player.profile_photo_url} alt={player.first_name} className="w-full h-full object-cover" />
          ) : (
            <span className="font-display text-2xl text-slate-500">
              {player.first_name?.[0]}{player.last_name?.[0]}
            </span>
          )}
        </div>

        {/* Identity */}
        <div className="flex-1 min-w-0">
          <div className="text-[9px] uppercase tracking-[0.2em] text-slate-500">Name</div>
          <div className="font-display text-base uppercase tracking-tight leading-tight">
            {player.first_name}
            <br />
            {player.last_name}
          </div>
          <div className="grid grid-cols-2 gap-1 mt-2 text-[10px]">
            <div>
              <div className="text-slate-500">Class</div>
              <div className="font-data text-sm">{player.graduation_year || "—"}</div>
            </div>
            <div>
              <div className="text-slate-500">Pos</div>
              <div className="font-data text-sm">{player.primary_position || "—"}</div>
            </div>
            {playerNumber != null && (
              <div className="col-span-2">
                <div className="text-slate-500">Player #</div>
                <div className="font-data text-base">#{String(playerNumber).padStart(3, "0")}</div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Camp */}
      {campTitle && (
        <div className="px-4 py-2 bg-[#F8F9FA] border-t border-slate-200">
          <div className="text-[9px] uppercase tracking-[0.2em] text-slate-500">Event</div>
          <div className="text-xs font-semibold text-[#0A192F] truncate">{campTitle}</div>
        </div>
      )}

      {/* QR */}
      <div className="px-4 py-4 border-t-2 border-[#E63946] flex items-center gap-3">
        <div className="bg-white p-1 border border-slate-200">
          <QRCodeSVG value={qrUrl} size={96} level="H" includeMargin={false} />
        </div>
        <div className="flex-1">
          <div className="text-[9px] uppercase tracking-[0.2em] text-slate-500">Scan to verify</div>
          <div className="text-[10px] text-slate-700 mt-1 leading-tight">
            Scan with any camera for check-in, profile, and verified metrics.
          </div>
          <div className="text-[8px] font-mono text-slate-400 mt-2 break-all">
            {player.qr_code_token?.slice(0, 8)}…
          </div>
        </div>
      </div>
    </div>
  );
}
